import { Role, RoleAssignment, subscribeRoleAssignmentFor } from '@adornis/abac/policies/SimpleRolePolicy.js';
import { subscribeAllEntities } from '@adornis/baseql/operations/mongo.js';
import { guardDialog } from '@adornis/dialog/dialog-functions.js';
import type { SectionRendererSection } from '@adornis/entity-relationship-management/client/details/SectionRenderer.js';
import { DropdownSelectionInput } from '@adornis/formfield/fields/dropdown-selection-input.js';
import { stateful, useState } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { AdornisUser } from '@adornis/users/db/a-user.js';
import { SmartTeamsCustomer } from '@nsc/smartteams-shared/db/SmartTeamsCustomer.js';
import { html, nothing } from 'lit';
import { getUserCustomerAssignments, setUserCustomerAssignments } from '../../api/customer-user-assignments.js';
import { subscribeCustomersPaginated } from '../../api/scoped-queries.js';
import { SmartTeamsRoleNames } from '../../features/permissions/roles-and-permissions.js';

/**
 * Section editor for linking a user to one or more SmartTeamsCustomer entities. The user
 * must already hold the Customer role; assignments persist on `SmartTeamsAppUser.customerIds`
 * (not on the role-assignment scope) — customer membership is a denormalised lookup table
 * for ticket scoping, not a permission context.
 */
const CustomerAssignmentEditor = stateful(({ user }: { user: AdornisUser<any> }) => {
  const [refreshKey, setRefreshKey] = useState(0);

  const rolesQuery = useQuery(() => subscribeAllEntities(Role)({ _id: 1, roleName: 1 }), []);
  const assignmentQuery = useQuery<RoleAssignment | null>(
    () => subscribeRoleAssignmentFor(user._id)({ _id: 1, roleIds: 1 }),
    [user._id, refreshKey],
  );
  const customersQuery = useQuery(
    () => subscribeCustomersPaginated(0, 1000, undefined)({ _id: 1, name: 1, serviceNowClientId: 1 }),
    [],
  );
  const linkedIdsQuery = useQuery(() => getUserCustomerAssignments(user._id)(), [user._id, refreshKey]);

  const roles = rolesQuery.value ?? [];
  const assignment = assignmentQuery.value;
  const customers = (customersQuery.value ?? []) as SmartTeamsCustomer[];
  const linkedIds = new Set((linkedIdsQuery.value ?? []) as string[]);

  const customerRole = roles.find(r => r.roleName === SmartTeamsRoleNames.CUSTOMER);
  const hasCustomerRole = customerRole && assignment?.roleIds?.includes(customerRole._id);

  if (rolesQuery.loading || assignmentQuery.loading) {
    return html`<span class="loading loading-spinner loading-sm text-primary"></span>`;
  }

  if (!hasCustomerRole) {
    return html`
      <p class="text-sm text-base-content/50">
        Only users with the role "Customer" can be linked to customer organisations.
      </p>
    `;
  }

  const linkedCustomers = customers.filter(c => linkedIds.has(c._id));
  const availableCustomers = customers.filter(c => !linkedIds.has(c._id));

  const persist = async (nextIds: string[]) => {
    await guardDialog(() => setUserCustomerAssignments(user._id, nextIds)({ _id: 1, customerIds: 1 }), {
      loadingText: 'Updating customer assignments…',
      successText: 'Customer assignments updated.',
      errorMessagePrefix: 'Customer assignments could not be updated',
    });
    setRefreshKey(refreshKey + 1);
  };

  const handleAdd = async (customer: SmartTeamsCustomer) => {
    if (linkedIds.has(customer._id)) return;
    await persist([...linkedCustomers.map(c => c._id), customer._id]);
  };

  const handleRemove = async (customerId: string) => {
    await persist(linkedCustomers.filter(c => c._id !== customerId).map(c => c._id));
  };

  const isLoading = customersQuery.loading || linkedIdsQuery.loading;

  return html`
    <div class="flex flex-col gap-3">
      <div class="flex items-center justify-between">
        <p class="text-xs text-base-content/70">
          Customer organisations whose tickets this user can view (read-only).
        </p>
        ${isLoading ? html`<span class="loading loading-spinner loading-sm text-primary"></span>` : nothing}
      </div>

      <div class="flex flex-wrap gap-2">
        ${linkedCustomers.length === 0
          ? html`<p class="text-sm text-base-content/50">No customers assigned</p>`
          : linkedCustomers.map(
              customer => html`
                <div class="badge badge-lg gap-2 py-3 pr-1">
                  <div class="flex items-center gap-2">
                    <div class="avatar placeholder">
                      <div
                        class="bg-secondary text-secondary-content rounded-full w-5 h-5 flex items-center justify-center"
                      >
                        <span class="text-[10px] font-bold">${(customer.name ?? '?')[0]?.toUpperCase() ?? '?'}</span>
                      </div>
                    </div>
                    <span>${customer.name}</span>
                    ${customer.serviceNowClientId
                      ? html`<span class="text-xs font-mono text-base-content/50"
                          >${customer.serviceNowClientId.slice(0, 8)}…</span
                        >`
                      : nothing}
                  </div>
                  <button
                    class="btn btn-ghost btn-xs btn-circle"
                    @click=${(e: Event) => {
                      e.stopPropagation();
                      void handleRemove(customer._id);
                    }}
                  >
                    <span class="icon-[mdi--close] text-sm"></span>
                  </button>
                </div>
              `,
            )}
      </div>

      ${availableCustomers.length > 0
        ? html`
            <div class="w-64">
              ${DropdownSelectionInput<SmartTeamsCustomer, false>({
                multi: false,
                placeholder: 'Add customer',
                selectables: availableCustomers,
                value: undefined,
                renderString: customer => customer.name ?? customer._id,
                resolveID: customer => customer._id,
                emptyState: 'No customers found',
                onPicked: value => {
                  if (value) void handleAdd(value as SmartTeamsCustomer);
                },
              })}
            </div>
          `
        : linkedCustomers.length > 0
          ? nothing
          : html`<p class="text-xs text-base-content/50">
              No customers exist yet. Create one in the Customers section first.
            </p>`}
    </div>
  `;
});

export const customerAssignmentSection: SectionRendererSection<AdornisUser> = {
  title: 'Customers',
  render: ({ entity: user }) => {
    if (!user) return nothing;
    return CustomerAssignmentEditor({ user });
  },
};
